// Reference data from Figma. Replace this fixture when dashboard endpoints exist.
export type SummaryMetric = {
  id: string
  label: string
  value: string
  icon: string
  tone?: 'critical' | 'warning'
  badge?: string
}
export type BestSeller = {
  rank: number
  name: string
  dose?: string
  sold: string
  amount: string
  remaining: string
}
export type RecentSale = {
  invoice: string
  time: string
  staff: string
  amount: string
  payment: string
}
export type StockAlert = {
  name: string
  minimum: number
  remaining: number
  urgent: boolean
}
export type ExpiringProduct = {
  name: string
  lot: string
  expiry: string
  urgent: boolean
}
export type SalesPeriodKey = 'today' | 'week' | 'month' | 'quarter' | 'year'

export type SalesPoint = {
  day: string
  amount: number
  current?: boolean
}

export type SalesPeriod = {
  label: string
  heading: string
  description: string
  data: SalesPoint[]
}

export const dashboardFixture = {
  branch: 'Main Office',
  dateLabel: '15 ต.ค. 2566',
  shiftStart: '08:30',
  roleLabel: 'หัวหน้าเภสัชกร',
  metrics: [
    {
      id: 'sales',
      label: 'ยอดขายวันนี้',
      value: '฿42,580.00',
      icon: 'kpi-sales',
      badge: '+12.5%',
    },
    {
      id: 'profit',
      label: 'กำไรวันนี้',
      value: '฿12,450.00',
      icon: 'kpi-profit',
      badge: '+5.2%',
    },
    {
      id: 'bills',
      label: 'จำนวนบิลวันนี้',
      value: '148 บิล',
      icon: 'kpi-bills',
    },
    {
      id: 'low-stock',
      label: 'สินค้าใกล้หมด',
      value: '12 รายการ',
      icon: 'kpi-low-stock',
      tone: 'critical',
      badge: 'วิกฤต',
    },
    {
      id: 'expiring',
      label: 'ใกล้หมดอายุ (30 วัน)',
      value: '5 รายการ',
      icon: 'kpi-expiring',
      tone: 'warning',
    },
  ] satisfies SummaryMetric[],
  salesPeriods: {
    today: {
      label: 'วันนี้',
      heading: 'ยอดขายวันนี้',
      description: 'ยอดขายรายชั่วโมง',
      data: [
        { day: '08:00', amount: 2300 },
        { day: '10:00', amount: 4800 },
        { day: '12:00', amount: 5200 },
        { day: '14:00', amount: 6400 },
        { day: '16:00', amount: 7500 },
        { day: '18:00', amount: 6800 },
        { day: '20:00', amount: 6200 },
        { day: '22:00', amount: 3380, current: true },
      ],
    },
    week: {
      label: 'สัปดาห์นี้',
      heading: 'ยอดขาย 7 วันล่าสุด',
      description: 'ยอดขายรายวัน',
      data: [
        { day: 'จ.', amount: 32000 },
        { day: 'อ.', amount: 38000 },
        { day: 'พ.', amount: 34000 },
        { day: 'พฤ.', amount: 45000, current: true },
        { day: 'ศ.', amount: 40000 },
        { day: 'ส.', amount: 28000 },
        { day: 'อา.', amount: 22000 },
      ],
    },
    month: {
      label: 'เดือนนี้',
      heading: 'ยอดขายเดือนนี้',
      description: 'ยอดขายรายวัน',
      data: Array.from({ length: 15 }, (_, index) => ({
        day: String(index + 1),
        amount: 6800 + ((index * 7919 + 4500) % 34000),
        current: index === 14,
      })),
    },
    quarter: {
      label: 'ไตรมาสนี้',
      heading: 'ยอดขายไตรมาสนี้',
      description: 'ยอดขายรายเดือน',
      data: [
        { day: 'ต.ค.', amount: 865000, current: true },
        { day: 'พ.ย.', amount: 0 },
        { day: 'ธ.ค.', amount: 0 },
      ],
    },
    year: {
      label: 'ปีนี้',
      heading: 'ยอดขายปีนี้',
      description: 'ยอดขายรายเดือน',
      data: [
        { day: 'ม.ค.', amount: 685000 },
        { day: 'ก.พ.', amount: 712000 },
        { day: 'มี.ค.', amount: 748000 },
        { day: 'เม.ย.', amount: 695000 },
        { day: 'พ.ค.', amount: 824000 },
        { day: 'มิ.ย.', amount: 790000 },
        { day: 'ก.ค.', amount: 866000 },
        { day: 'ส.ค.', amount: 910000 },
        { day: 'ก.ย.', amount: 875000 },
        { day: 'ต.ค.', amount: 942000, current: true },
        { day: 'พ.ย.', amount: 0 },
        { day: 'ธ.ค.', amount: 0 },
      ],
    },
  } satisfies Record<SalesPeriodKey, SalesPeriod>,
  bestSellers: [
    {
      rank: 1,
      name: 'Paracetamol',
      dose: '500mg',
      sold: 'ขายแล้ว 520 แผง',
      amount: '฿10,400',
      remaining: '1,200',
    },
    {
      rank: 2,
      name: 'Amoxicillin',
      dose: '500mg',
      sold: 'ขายแล้ว 185 กล่อง',
      amount: '฿7,400',
      remaining: '42',
    },
    {
      rank: 3,
      name: 'Chlorpheniramine',
      dose: '4mg',
      sold: 'ขายแล้ว 142 แผง',
      amount: '฿2,130',
      remaining: '800',
    },
    {
      rank: 4,
      name: 'Mask 3D (White)',
      sold: 'ขายแล้ว 98 ชิ้น',
      amount: '฿1,960',
      remaining: '150',
    },
  ] satisfies BestSeller[],
  recentSales: [
    {
      invoice: 'INV-661015-0148',
      time: '14:25',
      staff: 'เภสัชกร สมชาย',
      amount: '฿1,250.00',
      payment: 'Promptpay',
    },
    {
      invoice: 'INV-661015-0147',
      time: '14:10',
      staff: 'พนักงาน ก.',
      amount: '฿45.00',
      payment: 'เงินสด',
    },
    {
      invoice: 'INV-661015-0146',
      time: '13:55',
      staff: 'เภสัชกร สมชาย',
      amount: '฿2,400.00',
      payment: 'Credit Card',
    },
    {
      invoice: 'INV-661015-0145',
      time: '13:40',
      staff: 'พนักงาน ก.',
      amount: '฿180.00',
      payment: 'เงินสด',
    },
  ] satisfies RecentSale[],
  lowStock: [
    {
      name: 'Amoxycillin Syrup 125mg',
      minimum: 20,
      remaining: 4,
      urgent: true,
    },
    { name: 'Vitamin C 1000mg', minimum: 50, remaining: 12, urgent: false },
    { name: 'Alcohol Gel 450ml', minimum: 24, remaining: 8, urgent: false },
  ] satisfies StockAlert[],
  expiring: [
    {
      name: 'Gaviscon Liquid 10ml',
      lot: 'GV-0041',
      expiry: '30 ต.ค. 66',
      urgent: true,
    },
    {
      name: 'Mylanta 240ml',
      lot: 'MYL-882',
      expiry: '15 พ.ย. 66',
      urgent: true,
    },
    {
      name: 'Tylenol 500mg (100 tabs)',
      lot: 'TL-9901',
      expiry: '10 ม.ค. 67',
      urgent: false,
    },
    {
      name: 'Strepsils Honey & Lemon',
      lot: 'SS-410',
      expiry: '22 ก.พ. 67',
      urgent: false,
    },
  ] satisfies ExpiringProduct[],
}
export function matchesDashboardSearch(query: string, ...values: string[]) {
  const normalized = query.trim().toLocaleLowerCase()
  return (
    !normalized ||
    values.some((value) => value.toLocaleLowerCase().includes(normalized))
  )
}
